import { ChangeEvent, FC } from 'react';
import React from 'react';

type Props = {
  type?: 'text' | 'password' | 'date' | 'radio';
  clickable?: boolean;
  containerStyle?: React.CSSProperties;
  label?: string;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  placeholder?: string;
  icon?: JSX.Element;
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
  value?: string;
  name?: string;
  error?: string;
  touched?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void; // ✅ added for Formik
  options?: any[]; // ✅ for radio
  maxLength?: number
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  inputMode?: any;
  disabled?: any
};

export const Input: FC<Props> = ({
  placeholder,
  containerStyle,
  autoCapitalize = 'none',
  clickable,
  leftIcon,
  rightIcon,
  type = 'text',
  value = '',
  name = '',
  error,
  touched,
  onChange,
  onBlur,
  options = [],
  maxLength = 50,
  onKeyDown,
  inputMode,
  disabled
}) => {
  const hasError = touched && error;

  // ✅ Special handling for radio
  if (type === 'radio') {
    return (
      <div
        style={{
          padding: 5,
          marginBottom: 10,
          display: 'flex',
          flexDirection: 'column',
          ...containerStyle,
        }}
      >
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {options.map((option) => (
            <label
              key={option.id}
              style={{ display: 'flex', alignItems: 'center' }}
              className='t14'
            >
              <input
                type='radio'
                name={name}
                value={option.value}
                checked={value === option.value}
                onChange={onChange}
                onBlur={onBlur} // ✅ important for Formik touched state
                style={{ marginRight: '6px' }}
              />
              {option.label}
            </label>
          ))}
        </div>
        {hasError && (
          <div style={{ color: 'red', marginTop: 5 }} className='t14'>
            {error}
          </div>
        )}
      </div>
    );
  }

  // ✅ Default input
  return (
    <div
      style={{
        height: 50,
        padding: 5,
        marginBottom: hasError ? 45 : 20,
        flexDirection: 'column',
        position: 'relative',
        display: 'flex',
        backgroundColor: '#E9F3F6',
        borderRadius: 10,
        border: hasError ? '1px solid red' : 'none',
        ...containerStyle,
      }}
    >
      <div
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          display: 'flex',
        }}
      >
        {leftIcon && <div style={{ marginRight: 14 }}>{leftIcon}</div>}
        <input
          className='input-field'
          autoCapitalize={autoCapitalize}
          placeholder={placeholder}
          maxLength={maxLength}
          value={value}
          name={name}
          onKeyDown={onKeyDown}
          onChange={onChange}
          onBlur={onBlur} // ✅ important
          type={type}
          disabled={disabled}
          inputMode={inputMode}
          style={{
            width: '100%',
            height: '100%',
            padding: 0,
            margin: 0,
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
            fontSize: 16,
            color: 'var(--main-color)',
            fontFamily: 'DM Sans',
          }}
        />
        {rightIcon && (
          <div style={{ marginRight: 15, marginLeft: 10 }}>{rightIcon}</div>
        )}
      </div>
      {hasError && (
        <div
          style={{
            color: 'red',
            margin: 5,
            overflowWrap: 'break-word',
          }}
          className='t14'
        >
          {error}
        </div>
      )}
    </div>
  );
};
